import _slicedToArray from "/pubskape-frontend/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { __rest } from "tslib";
import * as React from 'react';
import styles from '@patternfly/react-styles/css/components/Drawer/drawer';
import { css } from '@patternfly/react-styles';
import { DrawerColorVariant, DrawerContext } from './Drawer';
import { formatBreakpointMods } from '../../helpers/util';
var isResizing = null;
var newSize = 0;
export var DrawerPanelContent = function DrawerPanelContent(_a) {
  var _a$className = _a.className,
      className = _a$className === void 0 ? '' : _a$className,
      id = _a.id,
      children = _a.children,
      _a$hasNoBorder = _a.hasNoBorder,
      hasNoBorder = _a$hasNoBorder === void 0 ? false : _a$hasNoBorder,
      _a$isResizable = _a.isResizable,
      isResizable = _a$isResizable === void 0 ? false : _a$isResizable,
      onResize = _a.onResize,
      minSize = _a.minSize,
      defaultSize = _a.defaultSize,
      maxSize = _a.maxSize,
      _a$increment = _a.increment,
      increment = _a$increment === void 0 ? 5 : _a$increment,
      _a$resizeAriaLabel = _a.resizeAriaLabel,
      resizeAriaLabel = _a$resizeAriaLabel === void 0 ? 'Resize' : _a$resizeAriaLabel,
      _a$resizeAriaDescribe = _a.resizeAriaDescribedBy,
      resizeAriaDescribedBy = _a$resizeAriaDescribe === void 0 ? 'Press space to begin resizing, and use the arrow keys to grow or shrink the panel. Press enter or escape to finish resizing.' : _a$resizeAriaDescribe,
      widths = _a.widths,
      _a$colorVariant = _a.colorVariant,
      colorVariant = _a$colorVariant === void 0 ? DrawerColorVariant.default : _a$colorVariant,
      props = __rest(_a, ["className", "id", "children", "hasNoBorder", "isResizable", "onResize", "minSize", "defaultSize", "maxSize", "increment", "resizeAriaLabel", "resizeAriaDescribedBy", "widths", "colorVariant"]);

  var panel = React.useRef();

  var _React$useContext = React.useContext(DrawerContext),
      position = _React$useContext.position,
      isExpanded = _React$useContext.isExpanded,
      isStatic = _React$useContext.isStatic,
      onExpand = _React$useContext.onExpand,
      drawerRef = _React$useContext.drawerRef;

  var hidden = isStatic ? false : !isExpanded;

  var _React$useState = React.useState(!hidden),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isExpandedInternal = _React$useState2[0],
      setIsExpandedInternal = _React$useState2[1];

  var currWidth = 0;
  var panelRect;
  var right;
  var left;
  var bottom;
  var setInitialVals = true;
  React.useEffect(function () {
    if (!isStatic && isExpanded) {
      setIsExpandedInternal(isExpanded);
    }
  }, [isStatic, isExpanded]);

  var handleTouchStart = function handleTouchStart(e) {
    e.stopPropagation();
    document.addEventListener('touchmove', callbackTouchMove, {
      passive: false
    });
    document.addEventListener('touchend', callbackTouchEnd);
    isResizing = true;
  };

  var handleMousedown = function handleMousedown(e) {
    e.stopPropagation();
    e.preventDefault();
    document.addEventListener('mousemove', callbackMouseMove);
    document.addEventListener('mouseup', callbackMouseUp);
    drawerRef.current.classList.add(css(styles.modifiers.resizing));
    isResizing = true;
    setInitialVals = true;
  };

  var handleMouseMove = function handleMouseMove(e) {
    var mousePos = position === 'bottom' ? e.clientY : e.clientX;
    handleControlMove(e, mousePos);
  };

  var handleTouchMove = function handleTouchMove(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    var touchPos = position === 'bottom' ? e.touches[0].clientY : e.touches[0].clientX;
    handleControlMove(e, touchPos);
  };

  var handleControlMove = function handleControlMove(e, controlPosition) {
    e.stopPropagation();

    if (!isResizing) {
      return;
    }

    if (setInitialVals) {
      panelRect = panel.current.getBoundingClientRect();
      right = panelRect.right;
      left = panelRect.left;
      bottom = panelRect.bottom;
      setInitialVals = false;
    }

    var mousePos = controlPosition;
    var newSize = 0;

    if (position === 'right') {
      newSize = right - mousePos;
    } else if (position === 'left') {
      newSize = mousePos - left;
    } else {
      newSize = bottom - mousePos;
    }

    if (position === 'bottom') {
      panel.current.style.overflowAnchor = 'none';
    }

    panel.current.style.setProperty('--pf-c-drawer__panel--md--FlexBasis', newSize + 'px');
    currWidth = newSize;
  };

  var handleMouseup = function handleMouseup() {
    if (!isResizing) {
      return;
    }

    drawerRef.current.classList.remove(css(styles.modifiers.resizing));
    isResizing = false;
    onResize && onResize(currWidth, id);
    setInitialVals = true;
    document.removeEventListener('mousemove', callbackMouseMove);
    document.removeEventListener('mouseup', callbackMouseUp);
  };

  var handleTouchEnd = function handleTouchEnd(e) {
    e.stopPropagation();

    if (!isResizing) {
      return;
    }

    isResizing = false;
    onResize && onResize(currWidth, id);
    document.removeEventListener('touchmove', callbackTouchMove);
    document.removeEventListener('touchend', callbackTouchEnd);
  };

  var callbackMouseMove = React.useCallback(handleMouseMove, []);
  var callbackTouchEnd = React.useCallback(handleTouchEnd, []);
  var callbackTouchMove = React.useCallback(handleTouchMove, []);
  var callbackMouseUp = React.useCallback(handleMouseup, []);

  var handleKeys = function handleKeys(e) {
    var key = e.key;

    if (key !== ' ' && key !== 'Escape' && key !== 'Enter' && key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      if (isResizing) {
        e.preventDefault();
      }

      return;
    }

    e.preventDefault();

    if (key === ' ' || key === 'Escape' || key === 'Enter') {
      if (key === ' ') {
        isResizing = true;
      } else {
        isResizing = false;
        onResize && onResize(currWidth, id);
      }

      var _panelRect = panel.current.getBoundingClientRect();

      newSize = position === 'bottom' ? _panelRect.height : _panelRect.width;
    }

    if (isResizing) {
      var delta = 0;

      if (key === 'ArrowRight') {
        delta = position === 'left' ? increment : -increment;
      } else if (key === 'ArrowLeft') {
        delta = position === 'left' ? -increment : increment;
      } else if (key === 'ArrowUp') {
        delta = increment;
      } else if (key === 'ArrowDown') {
        delta = -increment;
      }

      newSize = newSize + delta;

      if (position === 'bottom') {
        panel.current.style.overflowAnchor = 'none';
      }

      panel.current.style.setProperty('--pf-c-drawer__panel--md--FlexBasis', newSize + 'px');
      currWidth = newSize;
    }
  };

  var boundaryCssVars = {};

  if (defaultSize) {
    boundaryCssVars['--pf-c-drawer__panel--md--FlexBasis'] = defaultSize;
  }

  if (minSize) {
    boundaryCssVars['--pf-c-drawer__panel--md--FlexBasis--min'] = minSize;
  }

  if (maxSize) {
    boundaryCssVars['--pf-c-drawer__panel--md--FlexBasis--max'] = maxSize;
  }

  return React.createElement("div", Object.assign({
    id: id,
    className: css(styles.drawerPanel, isResizable && styles.modifiers.resizable, hasNoBorder && styles.modifiers.noBorder, formatBreakpointMods(widths, styles), colorVariant === DrawerColorVariant.light200 && styles.modifiers.light_200, className),
    ref: panel,
    onTransitionEnd: function onTransitionEnd(ev) {
      if (!hidden && ev.nativeEvent.propertyName === 'transform') {
        onExpand();
      }

      setIsExpandedInternal(!hidden);
    },
    hidden: hidden
  }, (defaultSize || minSize || maxSize) && {
    style: boundaryCssVars
  }, props), isExpandedInternal && React.createElement(React.Fragment, null, isResizable && React.createElement(React.Fragment, null, React.createElement("div", {
    className: css(styles.drawerSplitter, position !== 'bottom' && styles.modifiers.vertical),
    role: "separator",
    tabIndex: 0,
    "aria-orientation": position === 'bottom' ? 'horizontal' : 'vertical',
    "aria-label": resizeAriaLabel,
    "aria-describedby": resizeAriaDescribedBy,
    onMouseDown: handleMousedown,
    onKeyDown: handleKeys,
    onTouchStart: handleTouchStart
  }, React.createElement("div", {
    className: css(styles.drawerSplitterHandle),
    "aria-hidden": true
  })), React.createElement("div", {
    className: css(styles.drawerPanelMain)
  }, children)), !isResizable && children));
};
DrawerPanelContent.displayName = 'DrawerPanelContent';